import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Clock, Loader2, Mail, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

// =====================================================================
// Claim landing (3B-3n) — public route at /claim/:slug.
//
// The prospect lands here from the demo site's banner. We:
//   1. Fetch demo metadata (business name + expiresAt) and explain
//      what claiming gets them.
//   2. Collect their email and POST /api/claim/:slug, which issues a
//      magic link via Resend. The link includes a redirectAfter of
//      /claim/:slug/finish which transfers ownership after sign-in.
// =====================================================================

interface DemoInfo {
  businessName: string;
  slug: string;
  status: 'queued' | 'generating' | 'ready' | 'claimed' | 'expired' | 'failed';
  expiresAt: string;
}

type FormState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'sent'; email: string }
  | { kind: 'error'; message: string };

export function Claim() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const [demo, setDemo] = useState<DemoInfo | null>(null);
  const [demoError, setDemoError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [formState, setFormState] = useState<FormState>({ kind: 'idle' });

  const errorParam = params.get('error');

  useEffect(() => {
    if (!slug) return;
    void api
      .get<{ demo: DemoInfo }>(`/api/claim/${encodeURIComponent(slug)}`)
      .then((res) => setDemo(res.demo))
      .catch((err) => {
        const code = err instanceof Error ? err.message : 'unknown';
        setDemoError(code);
      });
  }, [slug]);

  const submit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (!slug) return;
    setFormState({ kind: 'submitting' });
    try {
      await api.post(`/api/claim/${encodeURIComponent(slug)}`, { email });
      setFormState({ kind: 'sent', email });
    } catch (err) {
      setFormState({
        kind: 'error',
        message: friendlyError(err instanceof Error ? err.message : 'unknown'),
      });
    }
  };

  if (demoError) {
    return <ClaimError code={demoError} />;
  }

  if (!demo) {
    return (
      <ClaimShell>
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading your site preview…
        </div>
      </ClaimShell>
    );
  }

  const daysLeft = Math.max(
    0,
    Math.round((new Date(demo.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
  );

  return (
    <ClaimShell>
      <header className="flex flex-col gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
          Pageboss demo
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          Claim your {demo.businessName} site
        </h1>
        <p className="text-base text-text-secondary">
          We built this site for you so you can see what it would look like — no
          credit card, no sign-up needed to preview it. To make it yours and start
          editing, claim it below.
        </p>
      </header>

      <ul className="grid gap-3 sm:grid-cols-3">
        <Bullet
          icon={<Shield className="h-4 w-4" />}
          title="30-day money-back"
          body="Cancel any time in the first 30 days for a full refund."
        />
        <Bullet
          icon={<Clock className="h-4 w-4" />}
          title={`${daysLeft} days to claim`}
          body="After that this preview goes away and we recycle the address."
        />
        {/* Honest funnel copy (fix wave, 2026-08-10 final review, Finding 4):
            claiming hands over the site in draft — going live is a separate,
            paid-gated launch (site-lifecycle #19, Task 1). Don't imply the
            claim itself publishes anything. */}
        <Bullet
          icon={<CheckCircle2 className="h-4 w-4" />}
          title="Edit immediately"
          body="Once you claim, the site is yours to edit — text, photos, pages. Launch it whenever you're ready and pick a plan."
        />
      </ul>

      {errorParam ? <ErrorBanner code={errorParam} /> : null}

      <section className="rounded-xl border border-border-subtle bg-card p-6 shadow-sm">
        {formState.kind === 'sent' ? (
          <SentState email={formState.email} />
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4">
            <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
              <span>Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourbusiness.com"
                disabled={formState.kind === 'submitting'}
                className={cn(
                  'h-11 px-3 rounded-md border border-border bg-card text-base text-foreground',
                  'placeholder:text-text-tertiary',
                  'focus-visible:outline-2 focus-visible:outline-brand-500 focus-visible:outline-offset-1',
                  'focus-visible:border-brand-500',
                  formState.kind === 'submitting' && 'opacity-60',
                )}
              />
              <span className="text-xs text-text-tertiary">
                We'll email you a one-tap claim link. No password needed.
              </span>
            </label>

            {formState.kind === 'error' ? (
              <p className="text-sm text-danger-fg" role="alert">
                {formState.message}
              </p>
            ) : null}

            {/* B4: outline until an email is typed — a filled button that
                cannot be pressed reads as the primary action while doing
                nothing. The submitting state keeps the fill (T8: never
                change variant mid-action). */}
            <Button
              type="submit"
              size="lg"
              variant={email ? 'default' : 'outline'}
              disabled={!email || formState.kind === 'submitting'}
              className="px-5"
            >
              {formState.kind === 'submitting' ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending claim link…
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4" />
                  Send me the claim link
                </>
              )}
            </Button>
          </form>
        )}
      </section>
    </ClaimShell>
  );
}

function ClaimShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl px-6 py-12 flex flex-col gap-8">
        <a
          href="/"
          className="inline-flex w-fit items-center text-sm font-semibold tracking-tight text-text-secondary hover:text-foreground"
        >
          pageboss
        </a>
        {children}
      </div>
    </main>
  );
}

function ClaimError({ code }: { code: string }) {
  const label = friendlyDemoError(code);
  return (
    <ClaimShell>
      <h1 className="text-2xl font-semibold tracking-tight">{label.title}</h1>
      <p className="text-text-secondary">{label.body}</p>
      <a
        href="/"
        className="inline-flex w-fit items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-800"
      >
        ← Back to pageboss.com
      </a>
    </ClaimShell>
  );
}

function ErrorBanner({ code }: { code: string }) {
  const label = friendlyDemoError(code);
  return (
    <div className="rounded-md bg-danger-bg px-4 py-3 text-sm text-danger-fg">
      <p className="font-medium">{label.title}</p>
      <p className="text-text-secondary mt-0.5">{label.body}</p>
    </div>
  );
}

function Bullet({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <li className="flex items-start gap-2.5 rounded-md border border-border-subtle bg-card p-3">
      <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700">
        {icon}
      </span>
      <div className="text-sm">
        <div className="font-medium text-foreground">{title}</div>
        <div className="text-text-secondary">{body}</div>
      </div>
    </li>
  );
}

function SentState({ email }: { email: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-4 text-center">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-success-bg text-success-fg">
        <Mail className="h-6 w-6" />
      </div>
      <h2 className="text-lg font-semibold tracking-tight">Check your inbox</h2>
      <p className="max-w-sm text-sm text-text-secondary">
        We sent a one-tap claim link to{' '}
        <span className="font-medium text-foreground">{email}</span>. Click it to
        take ownership of this site. The link works for 15 minutes.
      </p>
    </div>
  );
}

function friendlyError(code: string): string {
  switch (code) {
    case 'invalid_input':
      return 'That email doesn\'t look quite right — double-check and try again.';
    case 'not_found':
      return 'We couldn\'t find this demo. It may have been claimed or expired.';
    case 'already_claimed':
      return 'This site has already been claimed.';
    case 'demo_unavailable':
      return 'This demo is no longer available.';
    case 'email_failed':
      return 'Something went wrong sending the email. Please try again in a minute.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

function friendlyDemoError(code: string): { title: string; body: string } {
  switch (code) {
    case 'not_found':
      return {
        title: "We couldn't find this site",
        body: 'The demo URL may have expired or already been claimed. If you believe this is a mistake, reply to the email we sent you.',
      };
    case 'already_claimed':
      return {
        title: 'This site has already been claimed',
        body: 'If you claimed it, sign in to your dashboard at dash.pageboss.com.',
      };
    case 'demo_unavailable':
      return {
        title: 'This demo is no longer available',
        body: 'It may have expired. Reach out to us and we\'ll generate a fresh one.',
      };
    case 'session_required':
      return {
        title: 'Sign in first',
        body: "We need a quick email step to set up your account. Enter your email below to continue.",
      };
    case 'demo_not_ready':
      return {
        title: 'This demo isn\'t ready yet',
        body: 'Give us a few minutes — the site is still being generated. Refresh and try again.',
      };
    default:
      return {
        title: 'Something went wrong',
        body: 'Please try again, or reach out to us if the problem continues.',
      };
  }
}
